import translate from './translator';
import { convertParamsToHash, getParamsFromUrl } from './urlResolver';
import { parse, differenceInDays } from 'date-fns';
import { DEALS_PER_PAGE } from '../constants/constants';
import { pushEvent } from './analytics';
import { MOREIN_CITY_CLICK } from '../constants/analyticEvents';

export const getCookie = (name) => {
  let matches = document.cookie.replace('%=', '=').match(new RegExp(
    "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + "=([^;]*)"
  ));
  return matches ? decodeURIComponent(matches[1]) : undefined;
}

export const setCookie = (name, value, expdays) => {
  if (name && value) {
    const d = new Date();
    d.setTime(d.getTime() + 24*60*60*1000*expdays);
    document.cookie = name + "=" + value + ";path=/;expires=" + d.toGMTString();
  }
}

export const splitEvery = (array, length, unshiftElem = false) => {
  const newArr = [ ...array ];
  if (unshiftElem) {
    newArr.unshift(unshiftElem);
  }
  return newArr.reduce((result, item, index) => {
    if (index % length === 0) result.push([]);
    result[Math.floor(index / length)].push(item);

    return result;
  }, []);
}

export function convertMiliseconds(miliseconds, format) {
  let days, hours, minutes, seconds, total_hours, total_minutes, total_seconds;

  total_seconds = Math.floor(miliseconds / 1000);
  total_minutes = Math.floor(total_seconds / 60);
  total_hours = Math.floor(total_minutes / 60);
  days = Math.floor(total_hours / 24);

  seconds = total_seconds % 60;
  minutes = total_minutes % 60;
  hours = total_hours % 24;

  switch (format) {
    case 's':
      return total_seconds;
    case 'm':
      return total_minutes;
    case 'h':
      return total_hours;
    case 'd':
      return days;
    default:
      return { d: days, h: hours, m: minutes, s: seconds };
  }
}

export function getCheckedCheckboxes(checkboxes) {
  if (!Array.isArray(checkboxes)) return;
  return checkboxes.filter((item) => item.active).map((item) => item.id);
}

export function shallowEqual(object1, object2) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (let key of keys1) {
    if (object1[key] !== object2[key]) {
      return false;
    }
  }

  return true;
}

export function deepEqual(object1, object2) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
}

function isObject(object) {
  return object != null && typeof object === 'object';
}

export function parseDate(date, format = 'dd/MM/yyyy') {
  return parse(date, format, new Date());
}

export const getArray = (array) => {
  if (Array.isArray(array)) return array;

  if (array === undefined || array === null) return [];

  return [array];
};

export const isMobile = () => (window.innerWidth <= 768);

export const findInArray = (arr, value) => (arr.indexOf(value) > -1);

export const getReviewWord = (reviewScore) =>
  reviewScore <= 2
    ? translate('holidayFinder.dealItem.nice')
    : reviewScore <= 3.5
    ? translate('holidayFinder.dealItem.good')
    : reviewScore <= 4
    ? translate('holidayFinder.dealItem.very_good')
    : reviewScore <= 4.5
    ? translate('holidayFinder.dealItem.excelent')
    : reviewScore <= 5
    ? translate('holidayFinder.dealItem.extraordinary')
    : translate('holidayFinder.dealItem.extraordinary');

export const scrollToResults = () => {
  return new Promise((resolve) => {
    const scrollDuration = 300;


    setTimeout(() => {
      const scrollOffset = isMobile()
        ? -40
        : 65

      const element = document.getElementById('scrollToResults');
      if (!element) return;

      const position = element.offsetTop - scrollOffset;

      window.scrollTo({
        top: position,
        behavior: 'smooth',
      });
    }, 50)

    setTimeout(() => {
      resolve('scroll end');
    }, scrollDuration + 150);
  });
};

export const scrollToResultsFast = () => {
  const scrollOffset = isMobile()
  ? -40
  : 65

  const element = document.getElementById('scrollToResults');
  if (!element) return;

  const position = element.offsetTop - scrollOffset;

  window.scrollTo({
    top: position,
    behavior: 'instant',
  });
}

// TODO: To remove
export const getFavoritesFromLS = () => {
  return JSON.parse(localStorage.getItem('favoritesList')) || [];
};

export const isRtl = () => document.querySelector("html").getAttribute("dir") === "rtl";

export const isB2B = () => window.dataGlobalSettings.siteType === 3;

export const parseBoldText = (string) => {
  return string
    .replace(/&lt;b&gt;/gm, '*')
    .split('*')
    .map((item, index) => (
      index % 2 === 0
        ? item
        : <b key={index}>{item}</b>
    )
  );
};

export const searchInOneDestination = (destinationId) => (e) => {
  e.stopPropagation();

  const paramsFromUrl = getParamsFromUrl();
  delete paramsFromUrl.destinationsGroups;
  // delete paramsFromUrl.what;

  const updatedHash = convertParamsToHash({
    ...paramsFromUrl,
    destinations: destinationId,
  });

  pushEvent(MOREIN_CITY_CLICK, { destinationId });

  window.open(
    `${window.location.origin}${window.location.pathname}#${updatedHash}&moreIn=city`,
    "_blank"
  );
};

export const formatStaticDealsParams = (params) => {
  const { stops, stars, freeCancellation, board, facilities, sort, providerHotelIds } =
    params.filters;

  const activeFilters = {
    rating: stars?.filter((item) => item.active).map((item) => item.value),
    stops: stops?.filter((item) => item.active).map((item) => item.value),
    refundable: freeCancellation,
    board: board
      ?.filter((item) => item.active)
      .map((item) => item.value.toUpperCase()),
    amenities: facilities
      ?.filter((item) => item.active)
      .map((item) => item.value),
    providerHotelIds: providerHotelIds,
  };
  const activeSort = sort?.find((item) => item.active);

  return {
    ...params,
    filters: activeFilters,
    sort: { [activeSort?.label]: activeSort?.value },
    limit: DEALS_PER_PAGE,
    distance: 5, // TODO
    provider: 240, // TODO
  };
};

export const isExpiredTimestampInDays = (timestamp, days) => {
  return ((Date.now() - timestamp) / 1000 / 60 / 24) > days;
}

export const parseStringToJson = (string) => {
  if (!string) return;

  return JSON.parse(
    string
    .replace(/&quot;/gm, '"')
    .replace(/&lt;b&gt;/gm, '<b>')
    .replace(/&lt;\/b&gt;/gm, '</b>')
    .replace(/&lt;br\/&gt;/gm, '<br />')
    .replace(/&lt;\/b&gt;/gm, '</b>')
    .replace(/&#039;/gm, `'`)
  )
}

export const isDebugAllowed = () => {
  const hostName = window.location.host;
  if (
    hostName.indexOf('localhost') > -1 ||
    hostName.indexOf('.stage') > -1 ||
    (/\d/.test(hostName)) ||
    getCookie('hfdebug') ||
    getCookie('MxTestProject')
  ) {
    return true;
  }
  return false;
}

export const showFavoriteButton = () => {
  const favoriteButton = document.querySelector('#my-favourites-container a');

  if (favoriteButton) {
    favoriteButton.classList.remove('hidden');
  }
}

export const hideTwigSkeletons = (delay = 0) => {
  const skeleton = document.querySelector('.skeleton-without-offers');
  setTimeout(() => {
    if (skeleton) {
      const skeleton = document.querySelector('.skeleton-without-offers');
      skeleton.classList?.add('hide-skeleton');
    }
    if (skeleton) {
      const skeleton = document.querySelector('.skeleton-with-offers');
      skeleton.classList?.add('hide-skeleton');
    }
  }, delay);
}

export const highlightBoldText = (str, searchValue) => {
  let result = str;

  searchValue.split(/[ ,]/gi).forEach((value) => {
    const regex = new RegExp(value, "gi");

    if (regex.test(str) && value.length > 1) {
      const searchPosition = result.toLowerCase().indexOf(value.toLowerCase());

      result = `${result.slice(0, searchPosition)}<b>${result.slice(
        searchPosition,
        searchPosition + value.length
      )}</b>${result.slice(searchPosition + value.length)}`
    }
  });

  return result;
}

export const getUserToken = () => {
  const userContext = JSON.parse(localStorage.getItem('hf_user'));

  return userContext?.user?.token;
}

export const multiplyBestOutOf = (when, whatMultiply, bestOutOf) => {
  let bestOfDatesMultiplier = 90;
  let bestOfLOSMultiplier = 7;
  const bestOutOfMultiplied = {};

  if (when.type === 'month' && when.months.length > 0) {
    bestOfDatesMultiplier = when.months.length * 30;
  } else if ((when.type === 'range' || when.type === 'specific') && when.startDate && when.endDate) {
    const startDate = parse(when.startDate, 'dd/MM/yyyy', new Date()),
      endDate = parse(when.endDate, 'dd/MM/yyyy', new Date()),
      difference = differenceInDays(endDate, startDate);

    bestOfDatesMultiplier = difference;
  }

  if (when?.nights && when?.nights?.length > 0) {
    bestOfLOSMultiplier = when.nights.length;
  }

  if (whatMultiply === 'multiplyTotal') {
    return bestOutOf * bestOfDatesMultiplier * bestOfLOSMultiplier;
  }
  if (whatMultiply === "multiplyDestinations") {
    for (const prop in bestOutOf) {
      bestOutOfMultiplied[prop] = bestOutOf[prop] * bestOfDatesMultiplier * bestOfLOSMultiplier;
    }
    return bestOutOfMultiplied
  }
};
